package com.milta.feature.optimizationquota.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.optimizationquota.domain.entity.OptimizationQuota
import com.milta.feature.optimizationquota.domain.usecase.GetMyOptimizationHistoryUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

data class MyOptimizationsState(
    val quota: OptimizationQuota? = null,
    val isLoadingQuota: Boolean = true,
    /** "server_error" | "offline" — only shown while no counter is known. */
    val quotaError: String? = null,
    val history: OptimizationHistoryState = OptimizationHistoryState(),
)

sealed interface MyOptimizationsIntent {
    /** Opening the screen / "Réessayer": re-reads the counter and restarts the history at page 1. */
    data object Load : MyOptimizationsIntent
    /** The list reached its bottom — loads the next page if any (R13). */
    data object LoadMoreHistory : MyOptimizationsIntent
}

/**
 * MIL-97 — "Mes optimisations": the caller's own counter and its paged history. Shared by the
 * mobile Profile screen (Driver and Enterprise, M3) and the web "Paramètres" card (Enterprise, E3).
 */
class MyOptimizationsViewModel(
    private val quotaStore: OptimizationQuotaStore,
    private val getMyHistory: GetMyOptimizationHistoryUseCase,
) : MviViewModel<MyOptimizationsState, MyOptimizationsIntent>(MyOptimizationsState(quota = quotaStore.quota.value)) {

    init {
        quotaStore.quota.onEach { quota -> setState { copy(quota = quota ?: this.quota) } }.launchIn(scope)
        onIntent(MyOptimizationsIntent.Load)
    }

    override fun onIntent(intent: MyOptimizationsIntent) {
        when (intent) {
            MyOptimizationsIntent.Load -> load()
            MyOptimizationsIntent.LoadMoreHistory -> loadMore()
        }
    }

    /** Bumped by [load]: a page answer from before a reset ("Réessayer") is ignored. */
    private var historyGeneration = 0
    private var historyJob: Job? = null

    private fun load() {
        historyGeneration++
        historyJob?.cancel()
        setState { copy(isLoadingQuota = true, quotaError = null, history = OptimizationHistoryState()) }
        scope.launch {
            quotaStore.refresh().fold(
                onSuccess = { setState { copy(isLoadingQuota = false) } },
                onFailure = { e ->
                    setState { copy(isLoadingQuota = false, quotaError = if (e is ApiException) "server_error" else "offline") }
                },
            )
        }
        loadMore()
    }

    private fun loadMore() {
        val history = currentState.history
        if (history.isLoadingPage || !history.hasMore) return
        // Synchronous flag before launching: a second scroll event can't fetch the same page twice.
        setState { copy(history = history.copy(isLoadingPage = true, error = null)) }
        val generation = historyGeneration
        historyJob = scope.launch {
            val result = getMyHistory(history.nextPage, OPTIMIZATION_HISTORY_PAGE_SIZE)
            if (generation != historyGeneration) return@launch
            result.fold(
                onSuccess = { page -> setState { copy(history = this.history.withPage(page)) } },
                onFailure = { e -> setState { copy(history = this.history.withError(e)) } },
            )
        }
    }
}
